import { useEffect, useState } from 'react';
import "../../css/bootstrap.min.css";
import "../../css/animate.min.css";
import "../../css/style.css";
import "../../css/responsive.css";
import "../../css/light-color.css";
import "../../css/floatlabel.css";
import "../../css/customstyle.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Header from '../../components/sections/mainwebsite/Header';
import ScrollToTop from 'react-scroll-to-top';
import { Footer } from '../../components/sections/footer';
import { AxiosInstance } from '../../components/utils';
import MainWebFooter from '../../components/sections/mainwebsite/mainwebfooter';

interface GalleryItem {
  id: string;
  title: string;
  img: string;
  drivelink: string;
  createdate: string;
}

function Gallery() {
  const [events, setEvents] = useState<any[]>([]);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await AxiosInstance.get('/api/getevent?eventstatus=OPEN,REGISTRATIONSCLOSED,REGISTRATIONSOPEN');
        if (response.data.result === 'Success') {
          setEvents(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchGalleryItems = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch gallery items data from API
        const response = await AxiosInstance.get('/api/getgallery');
        
        if (response.data.result === 'Success' && response.data.data) {
          // Convert object to array and sort by date (newest first)
          const galleryArray = Object.entries(response.data.data)
            .map(([id, item]: [string, any]) => ({
              id,
              ...item
            }))
            .sort((a, b) => new Date(b.createdate).getTime() - new Date(a.createdate).getTime());
          
          setGalleryItems(galleryArray);
        } else {
          setGalleryItems([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching gallery items:', error);
        setError('Failed to load gallery items. Please try again later.');
        setLoading(false);
      }
    };

    fetchGalleryItems();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const renderGalleryItem = (item: GalleryItem) => (
    <div className="gallery-item" key={item.id}>
      <div className="gallery-item-inner">
        <div className="gallery-image">
          <img src={item.img} alt={item.title} />
        </div>
        <div className="gallery-content">
          <h3 className="gallery-title">{item.title}</h3>
          <div className="gallery-meta">
            <span className="gallery-date">{formatDate(item.createdate)}</span>
          </div>
          {item.drivelink && (
            <div className="gallery-actions">
              <a href={item.drivelink} target="_blank" rel="noopener noreferrer" className="btn btn-primary gallery-view-more">
                View Gallery
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>
        {`
          .gallery-item {
            background: #ffffff;
            border-radius: 12px;
            box-shadow: 0 6px 20px rgba(0,0,0,0.1);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            overflow: hidden;
            margin-bottom: 30px;
          }
          
          .gallery-item:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 30px rgba(0,0,0,0.15);
          }
          
          .gallery-item-inner {
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          
          .gallery-image {
            position: relative;
            overflow: hidden;
            height: 250px;
          }
          
          .gallery-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
          }
          
          .gallery-item:hover .gallery-image img {
            transform: scale(1.05);
          }
          
          .gallery-content {
            padding: 20px;
            flex-grow: 1;
            display: flex;
            flex-direction: column;
          }
          
          .gallery-title {
            font-size: 20px;
            font-weight: 700;
            color: #333;
            margin-bottom: 10px;
            line-height: 1.3;
          }
          
          .gallery-meta {
            display: flex;
            gap: 15px;
            margin-bottom: 20px;
            font-size: 14px;
            color: #666;
          }
          
          .gallery-date {
            color: #888;
          }
          
          .gallery-actions {
            margin-top: auto;
          }
          
          .gallery-view-more {
            background-color: #007bff;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            transition: background-color 0.3s ease;
          }
          
          .gallery-view-more:hover {
            background-color: #0056b3;
            color: white;
          }
          
          .gallery-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
            gap: 30px;
            padding: 20px 0;
          }
          
          .no-gallery {
            padding: 60px 0;
            text-align: center;
          }
          
          .no-gallery p {
            font-size: 18px;
            color: #666;
            font-style: italic;
          }
          
          .loading-spinner {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 200px;
          }
          
          .error-message {
            background: #f8d7da;
            color: #721c24;
            padding: 15px;
            border-radius: 6px;
            margin: 20px 0;
            border: 1px solid #f5c6cb;
          }
          
          @media (max-width: 768px) {
            .gallery-image {
              height: 200px;
            }
            
            .gallery-title {
              font-size: 18px;
            }
            
            .gallery-grid {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>
      
      <div className="antialiased light-vz circle-v ">
        <Header events={events} />
        <br></br>
        <h1 className='spacer-section'></h1>

        {/* Gallery Section */}
        <section className="gallery-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="sec-title white-heading-text">
                  <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                    <span className="fa-stack">
                      <i className="fa rhex fa-stack-2x"></i>
                      <i className="fa fa-camera fa-stack-1x"></i>
                    </span>
                  </span>
                  <h3 className="wow fadeInRight" data-wow-duration="500ms">
                    Gallery
                    <small> / Moments from our events</small>
                  </h3>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="loading-spinner">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="error-message">
                <i className="fa fa-exclamation-triangle"></i> {error}
              </div>
            ) : galleryItems.length > 0 ? (
              <div className="row">
                <div className="col-lg-12">
                  <div className="gallery-grid">
                    {galleryItems.map(renderGalleryItem)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-lg-12">
                  <div className="no-gallery">
                    <p>No gallery items available at the moment. Please check back later!</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <MainWebFooter />
        <ScrollToTop component={<i className="fa fa-angle-up"></i>} smooth />
      </div>
    </>
  );
}

export default Gallery;